import { Injectable, effect } from "@angular/core";
import { Subject } from "rxjs";
import { PatientActionsEntry } from "@apps/patient-core/src/app/data_model/patient-actions";
import { Constants } from "@apps/patient-core/constants";
import { E_Patient_Actions_Type, PatientActionBase } from "@backend/graph/patient_actions/patient-action-base";
import { GRAPHQL_OPERATION_NAMES, HttpService } from "../http.service";
import { MedicalHistoryService } from "../medical-history.service";
import { NavigationService, T_NavigationExtras } from "../navigation.service";
import { NhsPrService } from "../nhs-pr.service";
import Bugsnag from "@bugsnag/js";
import { NotificationService, NotificationTypes } from "../notification.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { CacheService } from "../cache.service";
import { E_TreatmentPlanStatus, TreatmentPlansService } from "../treatment-plans.service";
import { I_MissingPatientDetails } from "@backend/graph/patient-details/patient-details-base";
import { ActivatedRoute } from "@angular/router";
import { filterDefined } from "../../utils/rxjs";
import { PatientActionsCacheService } from "../patient-actions-cache.service";
import { compareArrays } from "@shared/utils";
import { BaseSessionService } from "./session.service";
import { BaseAuthService } from "./auth.service";

export interface I_PatientActions_SessionStorage {
  actions: Array<PatientActionsEntry>;
  expiry?: number;
}

export function PATIENT_ACTIONS_QUERY(short_code_id?: string | null) {
  const args = `${short_code_id ? `short_code_id: "${short_code_id}"` : ""}`;
  return `{
  patient_actions${args.length > 0 ? `(${args})` : ""} {
    items {
      appointment_ids
      text
      type
      pms_action_type
      required
      data {
        meta_data {
          medical_history_id
          exemption_id
          patient_exemption_code
          form_type
          treatment_plans {
            total_cost
          }
          smile_goal {
            goal
            timeframe
            motivation
          }
        }
        patient_details_missing_data {
          sections
          fields
        }
        patient_details_completed_percentage
        routine_appt_data {
          site_id
          id
          is_nhs
          category_id
          appointment_name
          appointment_type_id
        }
        estimates {
          id
          template_name
        }
      }
    }
  }
}`;
}

/**
 * Finds the action in the list which matches the same type, pms_action_type and appointment_ids
 */
function findMatchingAction(patientActions: Array<PatientActionsEntry>, action: PatientActionBase): PatientActionsEntry | undefined {
  return patientActions.find((potentialMatch) => {
    if (potentialMatch.type !== action.type) {
      return false;
    }
    if (potentialMatch.pms_action_type !== action.pms_action_type) {
      return false;
    }
    if (!compareArrays(potentialMatch.appointment_ids || [], action.appointment_ids || [])) {
      return false;
    }
    return true;
  });
}

export function isActionCompleted(newAction: PatientActionBase, foundCachedItem: PatientActionsEntry | undefined): boolean {
  // If we get a new action from the backend, use that as the source of truth
  if (newAction.required) return false;

  // If the action is already marked as completed then keep it as completed
  if (foundCachedItem?.completed) return true;

  // If the action was previously required but now isn't, its just been completed
  if (foundCachedItem?.required && !newAction.required) return true;

  return false;
}

export function handleNewActions(
  newPatientActionBases: Array<PatientActionBase>,
  existingPatientActions: Array<PatientActionsEntry>
): Array<PatientActionsEntry> {
  const newPatientActionEntries = new Array<PatientActionsEntry>();
  for (const newAction of newPatientActionBases) {
    const foundCachedItem = findMatchingAction(existingPatientActions, newAction);
    const newEntry = new PatientActionsEntry(newAction, foundCachedItem?.skipped || false);
    newEntry.completed = isActionCompleted(newAction, foundCachedItem);

    newPatientActionEntries.push(newEntry);
  }

  return newPatientActionEntries;
}

@Injectable()
export abstract class BasePatientActionsService {
  public onInitPatientActions: Subject<Array<PatientActionsEntry>> = new Subject();
  public onFetching: Subject<boolean> = new Subject();
  private _isFetching: boolean;
  constructor(
    protected _navigationService: NavigationService,
    protected _authService: BaseAuthService,
    protected _httpService: HttpService,
    protected _medicalHistoryService: MedicalHistoryService,
    protected _nhsPrService: NhsPrService,
    protected _notificationService: NotificationService,
    protected _patientsService: BasePatientsService,
    protected _treatmentPlansService: TreatmentPlansService,
    protected _cacheService: CacheService,
    protected _route: ActivatedRoute,
    protected _patientActionsCacheService: PatientActionsCacheService,
    protected _sessionService: BaseSessionService
  ) {
    this._treatmentPlansService.onTreatmentPlanEstimateChanged.pipe(filterDefined()).subscribe((data) => {
      if (data.status === E_TreatmentPlanStatus.SAVED) {
        if (data.estimate_id) this.markAsCompleted(E_Patient_Actions_Type.TREATMENT_PLAN_ESTIMATE, { estimateId: data.estimate_id });
      }
    });

    this._medicalHistoryService.onMedicalHistoryGetQuestionsChanged.pipe(filterDefined()).subscribe((data) => {
      if (data.status === "SAVED") this.markAsCompleted(E_Patient_Actions_Type.MEDICAL_HISTORY);
    });

    effect(() => {
      const isSaved = this._nhsPrService.saved();

      if (isSaved) {
        this.markAsCompleted(E_Patient_Actions_Type.NHS_PR);
      }
    });
  }

  public markAsCompleted(type: E_Patient_Actions_Type.TREATMENT_PLAN_ESTIMATE, data: { estimateId: number }): void;
  public markAsCompleted(type: Exclude<E_Patient_Actions_Type, E_Patient_Actions_Type.TREATMENT_PLAN_ESTIMATE>): void;
  public markAsCompleted(type: E_Patient_Actions_Type, data?: { estimateId: number }): void {
    const clonedActions = [...this.patientActions];
    const action = clonedActions.find((a) => a.type === type && a.required);
    if (!action) return;

    if (type === E_Patient_Actions_Type.TREATMENT_PLAN_ESTIMATE) {
      if (action.data.estimates) {
        //mark the specific action as completed
        const specificEstimate = action.data.estimates.find((e) => e.id === data?.estimateId);
        if (specificEstimate) {
          specificEstimate.completed = true;
        }

        //if all estimates have been completed, mark the action as completed
        if (action.data.estimates.filter((e) => !e.completed).length === 0) action.completed = true;
      }
    } else {
      action.completed = true;
    }
    this.patientActions = clonedActions;
    void this._checkIfAllActionsCompleted();
  }

  private async _checkIfAllActionsCompleted() {
    const all_completed = this.patientActions.every((action) => action.completed);
    const is_portal_user = this._patientsService.patientInfo?.registered_portal_user;
    if (all_completed && !is_portal_user && this._authService.isPatientUnauthenticated()) {
      await this._sessionService.clear();
    }
  }

  public get isFetching(): boolean {
    return this._isFetching;
  }

  public set isFetching(fetching: boolean) {
    this._isFetching = fetching;
    this.onFetching.next(fetching);
  }

  public setSkippableAction(type: E_Patient_Actions_Type): void {
    const actions = this.patientActions.map((action) => {
      if (action.type === type) action.skipped = true;
      return action;
    });
    this.patientActions = actions;
  }

  public fetchPatientActions(force = false) {
    if (this.isFetching || this._authService.isPublicAccess()) return;
    const is_expired = this._patientActionsCacheService.isExpired;

    if (!is_expired && !force) {
      this.onInitPatientActions.next(this.patientActions);
      return;
    }

    if (is_expired) this._cacheService.deleteSession(Constants.PATIENT_ACTIONS_SESSION_STORAGE_KEY);

    this.isFetching = true;
    this._httpService.query<any>(GRAPHQL_OPERATION_NAMES.PATIENT_ACTIONS, this.getPatientActionsQuery()).subscribe((response: any) => {
      if (!response.data.patient_actions) {
        this.isFetching = false;
        if (response.errors && response.errors.length > 0) {
          const message = "There was an error retrieving actions required for your appointment(s)";
          console.error(message, response.errors);

          this._notificationService.open({
            title: "Unexpected error",
            type: NotificationTypes.WARNING,
            body: message,
          });

          Bugsnag.notify(new Error(`${message} - c12fdba1`));
        }

        return;
      }

      const patient_actions = response.data.patient_actions.items;

      const patient_actions_entry = handleNewActions(patient_actions, this.patientActions);

      this.patientActions = patient_actions_entry;
      this.onInitPatientActions.next(patient_actions_entry);

      this.isFetching = false;
    });
  }

  public get patientActions(): Array<PatientActionsEntry> {
    return this._patientActionsCacheService.patientActions;
  }

  public set patientActions(actions: Array<PatientActionsEntry>) {
    this._patientActionsCacheService.patientActions = actions;
  }

  public get getIncompletedActionsCount(): number {
    if (!this.patientActions) return 0;
    return this.patientActions.filter((action) => !action.completed).length;
  }

  public get hasIncompletedActions(): boolean {
    return this.getIncompletedActionsCount > 0;
  }

  /**
   * Navigates to the route for the action to be completed by the user
   *
   * @param type Type of action we are navigating to
   */
  public abstract navigateToAction(action: PatientActionsEntry): void;

  protected abstract getPatientActionsQuery(): string;

  protected _handleNhsPrAction(action: PatientActionsEntry) {
    const { form_type, patient_exemption_code } = action.data?.meta_data || {};

    const route = this._nhsPrService.generateDefaultRoute(form_type, patient_exemption_code);
    this._goToFormPage(route);
  }

  protected _handlePatientDetailsAction(action: PatientActionsEntry) {
    const contact_details_initial_route = action.data.patient_details_missing_data?.sections?.[0] || "";
    this._goToFormPage(`my-dental/contact-details/edit/${contact_details_initial_route}`);
  }

  protected _navigateToTreatmentPlanEstimate(action: PatientActionsEntry): void {
    try {
      const uncompletedEstimates = action.data.estimates?.filter((estimate) => !estimate.completed);
      if (uncompletedEstimates?.length) {
        this._goToFormPage(`my-dental/treatment-plans/open/${uncompletedEstimates[0].id}`);
      } else {
        this._triggerBugsnagEstimateError(action);
      }
    } catch (e) {
      this._triggerBugsnagEstimateError(action, e);
    }
  }

  private _triggerBugsnagEstimateError(action: PatientActionsEntry, e?: any) {
    Bugsnag.leaveBreadcrumb("Unable to navigate to treatment plan estimate", {
      action,
      data: action.data,
      patient_id: this._patientsService.patientInfo?.id,
      site_id: this._patientsService.patientInfo?.site_id,
      error: e,
    });
    Bugsnag.notify(new Error("Unable to navigate to treatment plan estimate"));
  }

  public get hasUrlActionParam(): boolean {
    return this._route.snapshot?.queryParams?.fromAction === "true";
  }

  public get contactMissingDetails(): I_MissingPatientDetails | null {
    const contact_action = this.patientActions.find((action) => action.type === E_Patient_Actions_Type.PATIENT_DETAILS);
    return contact_action?.data.patient_details_missing_data || null;
  }

  public get estimateIds(): Array<number> | null {
    const estimate_action = this.patientActions.find((action) => action.type === E_Patient_Actions_Type.TREATMENT_PLAN_ESTIMATE);
    if (!estimate_action) return null;
    return estimate_action.data.estimates?.map((estimate) => estimate.id) || null;
  }

  public getActionAppointmentIds(type: E_Patient_Actions_Type): Array<string> {
    const action = this.patientActions.find((a) => a.type === type);
    return action?.appointment_ids || [];
  }

  protected _goToFormPage(path: string, params?: T_NavigationExtras) {
    this._navigationService.navigate(path, {
      originalDomain: true,
      queryParams: {
        fromAction: true,
      },
      ...params,
    });
  }
}
